<template>
  <div class="thread">
    <div class="d-flex py-3 flex-row">
      <forum-user
        ref="forum__user"
        :forum-date="threadDate"
        :user-forum="userThread"
        :learning-unit="learningUnit"
      >
      </forum-user>

      <div
        v-if="$currentUser && thread.isOwner($currentUser.id)"
        class="ml-auto ellipsis__thread-header"
      >
        <base-ellipsis ref="icon__dropdown-menu" class="icon__dropdown-menu btn-dropdown">
          <b-dropdown-item
            ref="dropdown-menu__edit"
            class="dropdown-menu__edit"
            @click.prevent="onEdit"
          >
            Edit
          </b-dropdown-item>

          <b-dropdown-item
            ref="dropdown-menu__delete"
            class="dropdown-menu__delete"
            @click.prevent="onDelete"
          >
            Delete
          </b-dropdown-item>
        </base-ellipsis>
      </div>
    </div>

    <div v-show="!isEditing" class="content-indent">
      <div v-if="thread.content" class="d-flex pb-3">
        <base-text-editor-display class="m-0 p-0">
          <span
            ref="text__thread-description"
            v-safe-html="thread.content"
            class="text__thread-description text__description"
          ></span>
        </base-text-editor-display>
      </div>

      <forum-thumbnail-group
        v-if="thread.images && thread.images.length"
        ref="thumbnail-group__thread"
        :key="`thumbnail-group-thred-${thread.id}`"
        :key-id="thread.id"
        :images="thread.images"
        :maximum-display-images="maximumDisplayImages"
        class="pb-3"
      ></forum-thumbnail-group>

      <div class="d-flex align-items-baseline pb-3">
        <div class="container__thread-comment-like">
          <like-button
            ref="button__like"
            :content="thread.liked"
            :count-like="thread.count_likes"
            @click:liked="like"
          />
          <div v-if="!isReply" class="button__comment d-flex align-items-center p-0">
            <font-awesome-icon :icon="{ prefix: 'far', iconName: 'message' }" />

            <span ref="text__total-comment" class="ml-2" v-text="thread.count_replies"></span>
          </div>

          <b-button
            v-if="!isReply"
            ref="button__reply"
            variant="link"
            class="button__reply p-0"
            @click="onReply = true"
          >
            Reply
          </b-button>
        </div>
      </div>

      <create-forum-reply
        v-if="onReply"
        ref="forum__reply"
        class="forum-reply"
        :forum-thread="thread"
        :learning-unit="learningUnit"
        :user="userAuth"
        @cancel="onReply = false"
        @submit:success="onSubmitSuccess"
      />
    </div>

    <div v-if="isEditing" class="content-indent">
      <edit-forum-thread
        ref="forum__edit-thread"
        :forum-thread="thread"
        @save="onEditSuccess"
        @cancel="onEditCancel"
      >
      </edit-forum-thread>
    </div>

    <delete-forum-thread-modal
      ref="forum__delete-thread-modal"
      :show.sync="isDeleting"
      :forum-thread="thread"
    ></delete-forum-thread-modal>
  </div>
</template>

<script>
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faMessage } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ForumThreads } from '@/models/ForumThreads';
import { ForumThreadUser } from '@/models/ForumThreadUser';
import BaseEllipsis from '@/components/base-components/BaseEllipsis';
import BaseTextEditorDisplay from '@/components/base-components/BaseTextEditorDisplay';
import ForumThumbnailGroup from '@/components/Forum/ForumThumbnailGroup';
import LikeButton from '@/views/Feed/components/LikeButton.vue';
import EditForumThread from './EditForumThread';
import DeleteForumThreadModal from './DeleteForumThreadModal';
import LearningUnit from '@/models/LearningUnit';
import CreateForumReply from '@/views/Feed/components/CreateForumReply.vue';
import ForumUser from '@/components/Forum/ForumUser.vue';
import User from '@/models/User';
import ForumLiker from '@/models/ForumLiker';

library.add(faMessage);

export default {
  components: {
    BaseEllipsis,
    FontAwesomeIcon,
    BaseTextEditorDisplay,
    ForumThumbnailGroup,
    LikeButton,
    EditForumThread,
    DeleteForumThreadModal,
    CreateForumReply,
    ForumUser
  },

  mixins: [datetimeMixin],

  props: {
    learningUnit: {
      type: LearningUnit,
      required: true
    },

    thread: {
      type: ForumThreads,
      required: true
    },

    userThread: {
      type: ForumThreadUser,
      required: true
    },

    maximumDisplayImages: {
      type: Number,
      default: 3
    }
  },

  data() {
    return {
      isEditing: false,
      isDeleting: false,
      onReply: false
    };
  },
  computed: {
    threadDate() {
      return this.thread.timestamp;
    },
    isReply() {
      return this.thread.main_forum_thread_id !== null;
    },
    userAuth() {
      return User.query().first();
    }
  },

  methods: {
    onEdit() {
      this.isEditing = true;
    },

    onEditSuccess() {
      this.isEditing = false;
      this.$emit('edit:success');
    },

    onEditCancel() {
      this.isEditing = false;
      this.$emit('edit:cancel');
    },

    async onDelete() {
      this.isDeleting = true;
      this.$emit('onDelete');
    },

    async onSubmitSuccess(v) {
      this.onReply = false;
      this.$emit('reply:success', v);
    },

    async like(content) {
      const { thread } = this,
        { id: forumThreadId } = thread;

      if (!content) {
        await ForumThreads.api().likeForumThread(forumThreadId);
      } else {
        await ForumThreads.api().unlikeForumThread(forumThreadId);
        ForumLiker.delete(content.id);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.user-detail {
  display: flex;
  flex-direction: row;
  gap: 0 0.5rem;
}

.text__user-detail {
  display: flex;
  flex-direction: column;

  > .text__user-name {
    color: var(--primary);
    font-weight: 600;
    font-size: 1rem;
  }

  > .text__user-role {
    color: var(--dark);
    font-weight: 400;
    font-size: 0.75rem;
    padding-left: 2px;
  }
}

.text__date {
  font-weight: 500;
  font-size: 1rem;
}

.content-indent {
  border-left: 1px solid var(--gray);
  margin-left: 22px;
  padding-left: 34px;
}

.container__thread-comment-like {
  display: flex;
  flex-direction: row;
  gap: 0 1rem;
}

.forum-reply {
  margin-top: 1rem;
}
</style>
